import { compileGameDefinition, type GameDefinition, type GameModule } from "@roqeet/core";

/**
 * 방탈출 — 데이터형 게임 모듈(결정 #29). 네이티브 모듈(text-adventure 등)과 달리
 * 게임 로직을 한 줄도 코딩하지 않는다. 아래 선언적 GameDefinition만으로
 * compileGameDefinition이 GameModule을 만들어내고, 그 모듈은 네이티브 모듈과
 * 완전히 동일한 계약(initialState/protocolInstructions/applyTurn/viewSpec)을 만족해
 * 전송 파이프라인·레지스트리·viewSpec 렌더러에 그대로 흐른다.
 *
 * 이 파일의 존재 의의: "코드 없이 데이터로 게임 제작"(#29~#35 사다리의 밑단)이
 * 실제로 동작함을 앱 안에서 실증한다. 향후 카드 IO(#6)로 이 정의를 가져오거나
 * 위저드/폼 에디터가 이 모양을 채우면, 같은 컴파일러를 거쳐 즉시 플레이 가능해진다.
 */
const escapeRoomDefinition: GameDefinition = {
  id: "escape-room",
  title: "방탈출",
  titleKey: "game.mod.escapeRoom.title",
  description: "단서를 모아 잠긴 문을 여는 AI 방탈출. (코드 없이 데이터로 정의된 게임)",
  descriptionKey: "game.mod.escapeRoom.desc",
  icon: "key",
  initialState: {
    clues: 0,
    cluesNeeded: 3,
    sanity: 100,
    maxSanity: 100,
    inventory: [],
    location: "어두운 방",
    escaped: false,
  },
  instructions: [
    "당신은 방탈출 게임의 진행자입니다. 플레이어는 잠긴 방에 갇혀 있고, 단서를 모아 탈출해야 합니다.",
    "장면을 2인칭으로 생생하게 묘사하고, 응답 끝에 RoQeet 게임 명령으로 상태를 갱신하세요(정확한 문법으로).",
    "- 선택지: @선택지 { ... } 블록에 2~4개를 한 줄씩 작성",
    "- 단서 발견: @증가 clues 1",
    '- 아이템: @추가 inventory "녹슨 열쇠"',
    '- 장소 이동: @설정 location = "서재"',
    "- 정신력 소모(함정·공포): @감소 sanity 10",
    "- 탈출 성공(단서를 모두 모아 문을 열면): @설정 escaped = true",
    "단서가 3개 모이면 탈출이 가능합니다. 정신력이 0이 되면 게임 오버입니다.",
  ].join("\n"),
  stateSummaryTemplate:
    "현재 — 위치: @(location), 단서: @(clues)/@(cluesNeeded), 정신력: @(sanity), 소지품: @(inventory)",
  // 보안(결정 #17): 모델은 이 키들만 변경 가능. 그 밖의 키를 건드리는 태그는 무시된다.
  mutableKeys: ["clues", "sanity", "inventory", "location", "escaped"],
  clamps: [
    { key: "sanity", min: 0, max: 100 },
    { key: "clues", min: 0 },
  ],
  endWhen: [
    {
      key: "escaped",
      op: "truthy",
      label: "탈출 성공",
      notice: "잠긴 문이 열렸습니다. 당신은 자유입니다!",
      tone: "success",
    },
    {
      key: "sanity",
      op: "lte",
      value: 0,
      label: "정신 붕괴",
      notice: "공포가 당신을 집어삼켰습니다. 게임 오버.",
      tone: "danger",
    },
  ],
  view: {
    statBars: [{ label: "Sanity", valueKey: "sanity", maxKey: "maxSanity", tone: "mp" }],
    statPanel: [
      { label: "Location", key: "location" },
      { label: "Clues", key: "clues" },
    ],
    inventoryKey: "inventory",
    inventoryTitle: "소지품",
    inventoryTitleKey: "game.labelInventory",
    showChoices: true,
  },
};

/** 컴파일된 방탈출 모듈(결정 #29). 레지스트리에 등록해 네이티브 모듈처럼 쓴다. */
export const escapeRoomModule: GameModule = compileGameDefinition(escapeRoomDefinition);
